import { buildCollection } from "@firecms/core";

import { MAX, eyebrow, heading, section, subheading } from "./sections";

// The Community & Stories page: one document (`pages/community`). Sections are
// declared in page order. Story cards reference testimonials, so their photo and
// quote are edited under Testimonials; this form owns which three are shown,
// each card's badge and the surrounding wording.

export const communityPageCollection = buildCollection({
  id: "page-community",
  path: "pages",
  name: "Community & Stories",
  singularName: "Community page",
  icon: "Favorite",
  group: "Pages",
  description:
    "The wording on the Community & Stories page. The stories themselves are edited under Testimonials.",
  // `pages` holds one document per page; this collection shows only Community's.
  forceFilter: { pageId: ["==", "community"] },
  // The page exists already and the site expects it — it can be edited, but
  // not added to or removed.
  permissions: { read: true, edit: true, create: false, delete: false },
  openEntityMode: "full_screen",
  hideIdFromForm: true,
  // Hidden from the drawer; the `pageShortcut` view in App.tsx opens it directly.
  hideFromNavigation: true,
  inlineEditing: false,
  exportable: false,

  properties: {
    hero: section(
      "1. Heard. Understood. Changed.",
      "The heading and the line of introduction at the top of the page.",
      {
        eyebrow: eyebrow(),
        heading: heading(
          "The large title at the top, e.g. Heard. Understood. Changed.",
        ),
        subheading: subheading(
          "The centred line under the title, introducing the stories below.",
        ),
      },
    ),

    stories: section(
      "2. The story cards",
      "The three cards under the heading. Pick whose story each one shows; the photo, the words and the names come from that person's testimonial, so they always match the sliding panel below.",
      {
        items: {
          name: "The three cards",
          dataType: "array",
          description:
            "One card each, left to right. There are always three — the layout is built around that, so they can be changed and reordered, but not added to.",
          canAddElements: false,
          sortable: true,
          validation: { min: 3, max: 3 },
          of: {
            dataType: "map",
            properties: {
              testimonial: {
                name: "Whose story",
                dataType: "reference",
                path: "testimonials",
                description:
                  "Search for the person, then click them to edit their photo, their words and their animal's name.",
                previewProperties: ["name", "animal"],
              },
              badge: {
                name: "Badge",
                dataType: "string",
                description:
                  "The small green badge in the corner of this card, e.g. Recommended. The tick in front of it is drawn by the site.",
                validation: { max: MAX.ctaLabel },
              },
            },
          },
        },
      },
    ),

    carousel: section(
      "3. More client voices",
      "The heading above the sliding panel at the bottom, which shows every testimonial in turn.",
      {
        heading: heading(
          "The title above the sliding panel, e.g. More client voices.",
        ),
      },
    ),

    // Identifies which page this document is; set once when seeded.
    pageId: {
      name: "Page id",
      dataType: "string",
      disabled: { hidden: true },
    },
    title: {
      name: "Page title",
      dataType: "string",
      disabled: { hidden: true },
    },
  },

  propertiesOrder: ["hero", "stories", "carousel"],
});
